/**
 * Hook para verificar permisos del usuario actual
 * NUEVO MODELO: Roles basados en email
 */

import { useMemo } from "react";
import { useAuthStore } from "@/stores/authStore";
import {
  hasPermission,
  canAccessModule,
  canManageModule,
  getAccessibleModules,
  getNavItemsForRole,
  getRoleScopeLevel,
  canViewScopeLevel,
  type Module,
  type Action,
  type ScopeLevel,
  type NavItem,
} from "@/lib/permissions";
import type { SystemRole, ClubType, Member } from "@/types";

/** Resultado del scope de datos */
export interface DataScope {
  type: "club" | "association" | "union" | "none";
  clubId?: string;
  clubIds?: string[];
  scopeId?: string;
  hasAccessToClub: (clubId: string) => boolean;
}

export interface UsePermissionsReturn {
  /** Rol principal del usuario */
  role: SystemRole | undefined;
  /** Nivel de scope del usuario */
  scopeLevel: ScopeLevel | undefined;
  /** ID del scope (clubId, associationId, unionId) */
  scopeId: string | undefined;
  /** Verifica si tiene permiso para una acción en un módulo */
  can: (module: Module, action?: Action) => boolean;
  /** Verifica si puede acceder a un módulo */
  canAccess: (module: Module) => boolean;
  /** Verifica si puede gestionar un módulo completamente */
  canManage: (module: Module) => boolean;
  /** Verifica si puede ver datos de cierto nivel */
  canViewLevel: (level: ScopeLevel) => boolean;
  /** Módulos accesibles */
  accessibleModules: Module[];
  /** Items de navegación filtrados */
  navItems: NavItem[];
  /** Es admin de unión */
  isUnionAdmin: boolean;
  /** Es admin de asociación */
  isAssociationAdmin: boolean;
  /** Es director de club */
  isClubDirector: boolean;
  /** Es miembro regular */
  isMember: boolean;
  /** Es cualquier tipo de admin */
  isAdmin: boolean;
  /**
   * Sección a la que está acotado el director (si aplica).
   * - undefined → director general (ve todo el club) o no es director.
   * - definido  → director de SOLO esa sección.
   */
  directorOfSection: ClubType | undefined;
  /** ¿Es director acotado a una sección concreta? */
  isSectionDirector: (clubId: string, section: ClubType) => boolean;
  /**
   * ¿Puede gestionar la sección X del club Y?
   * Equivale a: admin global O director general del club O director de esa sección.
   */
  canManageSection: (clubId: string, section: ClubType) => boolean;
  /**
   * ¿Puede VER (listar/abrir) este member?
   * Aplica filtro de sección si el usuario es director acotado.
   */
  canSeeMember: (
    member: Pick<Member, "clubId" | "section" | "staffSection">,
  ) => boolean;
}

export function usePermissions(): UsePermissionsReturn {
  const { profile } = useAuthStore();

  const role = profile?.primaryRole;
  const directorOfSection = profile?.directorOfSection;
  const directorClubId = profile?.directorOfClub?.id;

  return useMemo(() => {
    const scopeLevel = role ? getRoleScopeLevel(role) : undefined;
    const scopeId = profile?.scopeId;

    const isAdmin = role === "admin_union" || role === "admin_association";
    const isClubDirector = role === "club_director";

    const isSectionDirector = (clubId: string, section: ClubType) =>
      isClubDirector &&
      directorClubId === clubId &&
      directorOfSection === section;

    const canManageSection = (clubId: string, section: ClubType) => {
      if (isAdmin) return true;
      if (!isClubDirector || directorClubId !== clubId) return false;
      // Director general (sin section) maneja todas las secciones del club.
      if (!directorOfSection) return true;
      return directorOfSection === section;
    };

    const canSeeMember = (
      member: Pick<Member, "clubId" | "section" | "staffSection">,
    ) => {
      if (isAdmin) return true;
      if (!isClubDirector) return false;
      if (directorClubId !== member.clubId) return false;
      // Todos los directores del club (general o de sección) ven todos los
      // miembros. La UI muestra la sección de cada miembro y permite
      // filtrar; los directores de sección siguen limitados solo en
      // acciones sensibles (aprobar solicitudes de su sección, etc.).
      return true;
    };

    return {
      role,
      scopeLevel,
      scopeId,
      can: (module: Module, action: Action = "view") =>
        hasPermission(role, module, action),
      canAccess: (module: Module) => canAccessModule(role, module),
      canManage: (module: Module) => canManageModule(role, module),
      canViewLevel: (level: ScopeLevel) =>
        role ? canViewScopeLevel(role, level) : false,
      accessibleModules: getAccessibleModules(role),
      navItems: getNavItemsForRole(role),
      isUnionAdmin: role === "admin_union",
      isAssociationAdmin: role === "admin_association",
      isClubDirector,
      isMember: role === "member",
      isAdmin,
      directorOfSection,
      isSectionDirector,
      canManageSection,
      canSeeMember,
    };
  }, [role, profile?.scopeId, directorOfSection, directorClubId]);
}
